var __defProp = Object.defineProperty;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};

// src/api/index.ts
import axios from "axios";
import axiosRetry, { isNetworkError, isRetryableError, exponentialDelay } from "axios-retry";
import { logger } from "@getvim/vim-connect-logger";

// src/api/getSeamlessAuthUrlByEnv.ts
var envToSeamlessAuthUrl = {
  local: "http://localhost:2409/api",
  dev: "https://seamless-auth.dev.devim.io/api",
  staging: "https://seamless-auth.staging.getvim.com/api",
  demo: "https://seamless-auth.demo.getvim.com/api",
  prod: "https://seamless-auth.getvim.com/api"
};
function getSeamlessAuthUrlByEnv({ environment }) {
  return envToSeamlessAuthUrl[environment];
}

// src/api/index.ts
var RETRY_COUNT = 2;
var extractResponseData = ({ data }) => data;
var BaseSeamlessAuthApi = class {
  constructor({
    baseURL,
    timeout = 5e3,
    headers,
    environment
  }) {
    this.logger = logger.scope("seamless-auth-api");
    if (baseURL && environment) {
      throw new Error("BaseSeamlessAuthApi constructor - You should provide either baseURL or environment, not both");
    }
    const actualBaseUrl = environment ? getSeamlessAuthUrlByEnv({ environment }) : baseURL;
    this.http = axios.create({
      baseURL: actualBaseUrl,
      timeout,
      headers: __spreadValues({
        Accept: "application/json",
        "Content-Type": "application/json"
      }, headers != null ? headers : {})
    });
    axiosRetry(this.http, {
      retries: RETRY_COUNT,
      shouldResetTimeout: true,
      retryDelay: exponentialDelay,
      retryCondition: (error) => isNetworkError(error) || isRetryableError(error) || (error == null ? void 0 : error.code) === "ECONNABORTED"
    });
  }
  async getTicket(input) {
    const { ticketId, ehrUser } = input;
    const encodedEhrUser = encodeURIComponent(ehrUser);
    return extractResponseData(await this.http.get(`/v1/ticket?ticketId=${ticketId}&ehrUsername=${encodedEhrUser}`));
  }
  async postLogin(userData) {
    return extractResponseData(await this.http.post("/v1/login", userData));
  }
  async initialLogin(data, config) {
    return await extractResponseData(await this.http.post("/v1/login/initial", data, config));
  }
  async silentLogin(data, config) {
    const body = {
      organizationId: data.organizationId,
      ehrCredentials: {
        username: data.ehrCredentials.username,
        password: data.ehrCredentials.password
      }
    };
    return await extractResponseData(await this.http.post("/v1/login/silent", body, config));
  }
  async refreshTokenLogin(data, config) {
    const { refreshToken, isFirstLogin, organizationId, ehrUsername } = data;
    const body = {
      refreshToken,
      isFirstLogin,
      organizationId,
      ehrUsername
    };
    return await extractResponseData(await this.http.post("/v1/login/refresh-token", body, config));
  }
  async changePassword(input, config) {
    const { vimCredentials, newPassword, organizationId, userType } = input;
    const body = {
      vimCredentials,
      newPassword,
      organizationId,
      userType
    };
    return await extractResponseData(await this.http.post("/v1/auth/change-password", body, config));
  }
  async seamlessApproved(input, config) {
    const { organizationId, username, autoApproved } = input;
    const body = {
      organizationId,
      username,
      autoApproved
    };
    return await extractResponseData(await this.http.post("/v1/auth/seamless-approved", body, config));
  }
  async sendOtp(input, config) {
    const { organizationId, ehrUser, contactDetailType } = input;
    const body = {
      organizationId,
      ehrUser,
      contactDetailType
    };
    return await extractResponseData(await this.http.post("/v1/auth/send-otp", body, config));
  }
  async postResetPasswordLink(input, config) {
    const { username, organizationId, contactDetailType, userType } = input;
    const body = {
      username,
      organizationId,
      contactDetailType,
      userType
    };
    return await extractResponseData(await this.http.post("/v1/auth/reset-password-link", body, config));
  }
  async idTokenLogin(input, config) {
    return await extractResponseData(await this.http.post("/v1/login/id-token", input, config));
  }
};

// src/types/enums/index.ts
var TicketStatus = /* @__PURE__ */ ((TicketStatus2) => {
  TicketStatus2["APPROVED"] = "approved";
  TicketStatus2["DENIED"] = "denied";
  TicketStatus2["PENDING"] = "pending";
  TicketStatus2["EXPIRED"] = "expired";
  return TicketStatus2;
})(TicketStatus || {});

// src/types/interfaces/getLoginTicket.ts
var getLoginTicket_exports = {};

// src/types/interfaces/postLoginCreateTicket.ts
var postLoginCreateTicket_exports = {};

// src/types/interfaces/initialLogin.ts
var initialLogin_exports = {};

// src/types/interfaces/silentLogin.ts
var silentLogin_exports = {};

// src/types/interfaces/refreshTokenLogin.ts
var refreshTokenLogin_exports = {};

// src/types/interfaces/changePassword.ts
var changePassword_exports = {};

// src/types/interfaces/seamlessLoginApproved.ts
var seamlessLoginApproved_exports = {};

// src/types/interfaces/sendOtp.ts
var sendOtp_exports = {};

// src/types/interfaces/postResetPasswordLink.ts
var postResetPasswordLink_exports = {};

// src/types/interfaces/idTokenLogin.ts
var idTokenLogin_exports = {};
export {
  BaseSeamlessAuthApi,
  changePassword_exports as ChangePassword,
  getLoginTicket_exports as GetLoginTicket,
  idTokenLogin_exports as IdTokenLogin,
  initialLogin_exports as InitialLogin,
  postLoginCreateTicket_exports as PostLoginCreateTicket,
  postResetPasswordLink_exports as PostPasswordResetEmail,
  refreshTokenLogin_exports as RefreshTokenLogin,
  seamlessLoginApproved_exports as SeamlessLoginApproved,
  sendOtp_exports as SendOtp,
  silentLogin_exports as SilentLogin,
  TicketStatus
};
//# sourceMappingURL=index.js.map
